import React, { useState } from 'react';
import { Modal, ConfirmDialog } from '../../components/modals';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../components/buttons';
import { Input, TextArea } from '../../components/inputs';

/**
 * ModalShowcase
 * Displays modal components with interactive examples
 */
import { surfaces, textColors, typography, borders } from '../../tokens';
const ModalShowcase = () => {
  const [modals, setModals] = useState({
    small: false,
    medium: false,
    large: false,
    xl: false,
    form: false,
    confirmInfo: false,
    confirmWarning: false,
    confirmDanger: false,
    confirmSuccess: false,
  });

  const openModal = (name) => {
    setModals(prev => ({ ...prev, [name]: true }));
  };

  const closeModal = (name) => {
    setModals(prev => ({ ...prev, [name]: false }));
  };

  const handleConfirm = (type) => {
    alert(`${type} confirmed!`);
    closeModal(type);
  };

  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Modal Sizes */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Modal Sizes</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Modals support multiple size variants for different content needs.
        </p>

        <div className="flex flex-wrap gap-4 mb-6">
          <PrimaryButton onClick={() => openModal('small')}>
            Small Modal
          </PrimaryButton>
          <PrimaryButton onClick={() => openModal('medium')}>
            Medium Modal (Default)
          </PrimaryButton>
          <PrimaryButton onClick={() => openModal('large')}>
            Large Modal
          </PrimaryButton>
          <PrimaryButton onClick={() => openModal('xl')}>
            XL Modal
          </PrimaryButton>
        </div>

        {/* Small Modal */}
        <Modal
          isOpen={modals.small}
          onClose={() => closeModal('small')}
          title="Small Modal"
          size="sm"
          footer={
            <>
              <SecondaryButton onClick={() => closeModal('small')}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => closeModal('small')}>Confirm</PrimaryButton>
            </>
          }
        >
          <p className="${textColors.secondary}">
            This is a small modal. Perfect for simple confirmations or short messages.
          </p>
        </Modal>

        {/* Medium Modal */}
        <Modal
          isOpen={modals.medium}
          onClose={() => closeModal('medium')}
          title="Medium Modal"
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => closeModal('medium')}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => closeModal('medium')}>Confirm</PrimaryButton>
            </>
          }
        >
          <p className="${textColors.secondary} mb-4">
            This is a medium modal (default size). Good for most use cases including forms and detailed content.
          </p>
          <p className="${textColors.secondary}">
            It provides a good balance between content space and not overwhelming the page.
          </p>
        </Modal>

        {/* Large Modal */}
        <Modal
          isOpen={modals.large}
          onClose={() => closeModal('large')}
          title="Large Modal"
          size="lg"
          footer={
            <>
              <SecondaryButton onClick={() => closeModal('large')}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => closeModal('large')}>Confirm</PrimaryButton>
            </>
          }
        >
          <p className="${textColors.secondary} mb-4">
            This is a large modal. Use for complex forms, detailed information, or when you need more space.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm ${textColors.primary}">
              Large modals are perfect for:
            </p>
            <ul className="list-disc list-inside text-sm ${textColors.secondary} mt-2 space-y-1">
              <li>Multi-step forms</li>
              <li>Rich text editors</li>
              <li>Data tables</li>
              <li>Complex configurations</li>
            </ul>
          </div>
        </Modal>

        {/* XL Modal */}
        <Modal
          isOpen={modals.xl}
          onClose={() => closeModal('xl')}
          title="Extra Large Modal"
          size="xl"
          footer={
            <>
              <SecondaryButton onClick={() => closeModal('xl')}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => closeModal('xl')}>Confirm</PrimaryButton>
            </>
          }
        >
          <p className="${textColors.secondary} mb-4">
            This is an extra large modal. Best for very content-rich interfaces or when displaying large amounts of data.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-heading font-bold ${textColors.brightest} mb-2">Column 1</h4>
              <p className="text-sm ${textColors.secondary}">
                Extra large modals can accommodate multi-column layouts and complex UI structures.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-heading font-bold ${textColors.brightest} mb-2">Column 2</h4>
              <p className="text-sm ${textColors.secondary}">
                This gives you maximum flexibility for rich content presentation.
              </p>
            </div>
          </div>
        </Modal>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Modal } from '../design-system';

<Modal
  isOpen={isOpen}
  onClose={handleClose}
  title="Modal Title"
  size="lg"
  footer={
    <>
      <SecondaryButton onClick={handleClose}>
        Cancel
      </SecondaryButton>
      <PrimaryButton onClick={handleSave}>
        Save
      </PrimaryButton>
    </>
  }
>
  <p>Modal content goes here</p>
</Modal>`} />
        </div>
      </section>

      {/* Form Modal */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Modal with Form</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Example of a modal containing a form.
        </p>

        <div className="mb-6">
          <PrimaryButton onClick={() => openModal('form')}>
            Open Form Modal
          </PrimaryButton>
        </div>

        <Modal
          isOpen={modals.form}
          onClose={() => closeModal('form')}
          title="Create New Project"
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => closeModal('form')}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => {
                alert('Form submitted!');
                closeModal('form');
              }}>
                Create Project
              </PrimaryButton>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium ${textColors.primary} mb-1">
                Project Name
              </label>
              <Input placeholder="Enter project name" />
            </div>
            <div>
              <label className="block text-sm font-medium ${textColors.primary} mb-1">
                Description
              </label>
              <TextArea placeholder="Describe your project..." rows={3} />
            </div>
            <div>
              <label className="block text-sm font-medium ${textColors.primary} mb-1">
                Project Type
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>Personal</option>
                <option>Team</option>
                <option>Enterprise</option>
              </select>
            </div>
          </div>
        </Modal>
      </section>

      {/* Confirm Dialogs */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Confirm Dialogs</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Specialized modals for confirmation actions with semantic variants.
        </p>

        <div className="flex flex-wrap gap-4 mb-6">
          <PrimaryButton onClick={() => openModal('confirmInfo')}>
            Info Confirm
          </PrimaryButton>
          <PrimaryButton onClick={() => openModal('confirmWarning')}>
            Warning Confirm
          </PrimaryButton>
          <DangerButton onClick={() => openModal('confirmDanger')}>
            Danger Confirm
          </DangerButton>
          <PrimaryButton onClick={() => openModal('confirmSuccess')}>
            Success Confirm
          </PrimaryButton>
        </div>

        {/* Info Confirm */}
        <ConfirmDialog
          isOpen={modals.confirmInfo}
          onClose={() => closeModal('confirmInfo')}
          onConfirm={() => handleConfirm('confirmInfo')}
          title="Information"
          message="This is an informational confirmation dialog. It provides neutral information to the user."
          variant="info"
          confirmText="Got it"
          cancelText="Cancel"
        />

        {/* Warning Confirm */}
        <ConfirmDialog
          isOpen={modals.confirmWarning}
          onClose={() => closeModal('confirmWarning')}
          onConfirm={() => handleConfirm('confirmWarning')}
          title="Warning"
          message="This action requires your attention. Please review carefully before proceeding."
          variant="warning"
          confirmText="Proceed"
          cancelText="Cancel"
        />

        {/* Danger Confirm */}
        <ConfirmDialog
          isOpen={modals.confirmDanger}
          onClose={() => closeModal('confirmDanger')}
          onConfirm={() => handleConfirm('confirmDanger')}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          variant="danger"
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Success Confirm */}
        <ConfirmDialog
          isOpen={modals.confirmSuccess}
          onClose={() => closeModal('confirmSuccess')}
          onConfirm={() => handleConfirm('confirmSuccess')}
          title="Success!"
          message="Your changes have been saved successfully. Would you like to continue?"
          variant="success"
          confirmText="Continue"
          cancelText="Close"
        />

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { ConfirmDialog } from '../design-system';

<ConfirmDialog
  isOpen={confirmOpen}
  onClose={() => setConfirmOpen(false)}
  onConfirm={handleDelete}
  title="Delete Item"
  message="Are you sure? This action cannot be undone."
  variant="danger"
  confirmText="Delete"
  cancelText="Cancel"
/>`} />
        </div>
      </section>

      {/* Features */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Modal Features</h2>
        <div className="${surfaces.primary} rounded-lg border ${borders.default} p-6">
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">Portal Rendering:</span>
                <span className="${textColors.secondary}"> Modals render in a portal for proper z-index layering</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">Focus Trap:</span>
                <span className="${textColors.secondary}"> Tab navigation stays within the modal</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">ESC Key:</span>
                <span className="${textColors.secondary}"> Press ESC to close the modal</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">Overlay Click:</span>
                <span className="${textColors.secondary}"> Click outside to close</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">Body Scroll Lock:</span>
                <span className="${textColors.secondary}"> Background scroll is disabled when modal is open</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <div>
                <span className="font-medium ${textColors.brightest}">Animations:</span>
                <span className="${textColors.secondary}"> Smooth entrance and exit animations with Framer Motion</span>
              </div>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default ModalShowcase;
